/*
 * Navigation inbox showing conversations, invitations and active chapter activity.
 * Used by AppNavigation with the application's shared inbox source.
 * Keeps disclosure, keyboard focus and scroll local while shared reads survive route changes.
 * Example: registerInboxMenu(registry);
 *          <InboxMenu context={context} source={inbox} openConversation={openChat}/>
 */

import { UIElement, defineUIElements } from '../../runtime/components/ui-element.js';
import { Icon } from '../../runtime/components/presentation.jsx';
import { servicesContext } from '../../runtime/services.js';
import { conversationLabel } from '../../runtime/conversation-label.js';
import { conversationPreview } from '../../lib/messaging/conversation-preview.js';
import { shouldShowInboxMenuConversation } from '../../lib/messaging/inbox-unread.js';
import { ChapterActivity, prepareChapterActivity } from '../chapters/chapter-activity-menu.jsx';
import { positionNavigationMenu } from './menu-position.js';

function continuumConversationHref(conversation) {
  if (!conversation.continuum_id) return null;
  const tabs = {
    forum: 'forum',
    continuum_announce: 'announcements',
    continuum_staff_forum: 'staff-forum'
  };
  const tab = tabs[conversation.type];
  return tab ? `/continuums/${conversation.continuum_id}#${tab}` : null;
}

function sortedConversations(snapshot, reads) {
  const pinned = ['app_announce', 'continuum_announce', 'continuum_staff_forum', 'system'];
  const latest = (conversation) =>
    snapshot.latestSeqs.get(conversation.id) ?? conversation.latest_seq ?? 0;
  return snapshot.conversations
    .filter((conversation) =>
      shouldShowInboxMenuConversation(
        conversation,
        latest(conversation),
        reads.getSeq(conversation.id)
      )
    )
    .sort((a, b) => {
      const ai = pinned.indexOf(a.type),
        bi = pinned.indexOf(b.type);
      if (ai >= 0 || bi >= 0) {
        if (ai < 0) return 1;
        if (bi < 0) return -1;
        if (ai !== bi) return ai - bi;
        return (
          (a.continuum_name || '').localeCompare(b.continuum_name || '') || a.id.localeCompare(b.id)
        );
      }
      return latest(b) - latest(a) || (b.created_at || '').localeCompare(a.created_at || '');
    })
    .slice(0, 16);
}

export class InboxMenu extends UIElement {
  static properties = {
    source: { attribute: false },
    openConversation: { attribute: false },
    leftConversation: { attribute: false },
    startChat: { attribute: false },
    snapshot: { state: true },
    open: { state: true },
    visited: { state: true }
  };

  constructor() {
    super();
    this.open = this.visited = false;
    this.records = this.invitations = [];
    this.chapters = { rows: [] };
    this.captureContainer = (node) => (this.container = node);
    this.captureTrigger = (node) => (this.trigger = node);
    this.captureMenu = (node) => (this.menu = node);
    this.bindMethods('close', 'navigate');
    this.own(() => this.stopSource?.());
  }

  willUpdate(changed) {
    super.willUpdate(changed);
    servicesContext(this.context, ['auth', 'reads', 'a11y', 'api', 'dialogs']);

    if (this.observedSource !== this.source) {
      this.stopSource?.();
      this.observedSource = this.source;
      this.snapshot = this.source.get();
      this.stopSource = this.source.subscribe(
        this.guard(() => {
          this.snapshot = this.source.get();
        })
      );
    }

    const identity = this.context.services.auth.getAuthContext();
    if (this.configured && this.identity !== identity) {
      this.open = this.visited = false;
      this.records = this.invitations = [];
      this.chapters = { rows: [] };
    }
    this.identity = identity;
    this.configured = true;

    this.measurement = this.measure();
    if (!this.visited) return;
    const { auth, reads, identities } = this.context.services;
    const user = auth.getUser();
    this.invitations = this.snapshot.invitations.map((invitation) => ({
      key: invitation.conversation.id,
      others: (invitation.conversation?.members || [])
        .filter((member) => member.user_id !== user?.id)
        .map((member) => '@' + (member.username || 'unknown'))
        .join(', '),
      blocked: (invitation.blocked_members || [])
        .map((member) => '@' + (member.username || 'unknown'))
        .join(', ')
    }));

    this.records = sortedConversations(this.snapshot, reads).map((conversation) => {
      const message = conversation.latest_message;
      this.context.recordWork?.(
        'derived.conversationPreview',
        `${this._workKey}:${conversation.id}`
      );
      if (message && !message.redacted) servicesContext(this.context, ['markdown']);

      const latestSeq =
        this.snapshot.latestSeqs.get(conversation.id) ?? conversation.latest_seq ?? 0;
      return {
        conversation,
        latestSeq,
        label: conversationLabel(conversation, user, identities?.identitySnapshot),
        preview: conversationPreview(conversation, user, this.context),
        hasUnread: latestSeq > reads.getSeq(conversation.id),
        canLeave: ['dm', 'group'].includes(conversation.type)
      };
    });

    this.chapters = prepareChapterActivity(this.snapshot.chapters);
  }

  updated(changed) {
    super.updated(changed);
    const life = this.connection;
    if (!life || !this.menu) return;
    if (life !== this.boundConnection) {
      this.boundConnection = life;
      const outside = life.guard((event) => {
        if (!this.container.contains(event.target)) this.close();
      });
      const resize = life.guard(() => {
        if (this.open) positionNavigationMenu(this.trigger, this.menu, this.context.window);
      });
      this.context.document.addEventListener('click', outside);
      life.own(() => this.context.document.removeEventListener('click', outside));
      this.context.window.addEventListener('resize', resize);
      life.own(() => this.context.window.removeEventListener('resize', resize));
    }

    if (this.open) {
      this.restore(this.measurement);
      positionNavigationMenu(this.trigger, this.menu, this.context.window);
    }
    this.measurement = null;
  }

  measure() {
    if (!this.menu || !this.open) return null;
    const active = this.ownerDocument.activeElement;
    return {
      top: this.menu.scrollTop,
      restore: this.context.services.a11y.captureFocus(this.menu),
      conversation: active?.closest('[data-conversation-id]')?.dataset.conversationId,
      chapter: active?.closest('[data-chapter-id]')?.dataset.chapterId,
      rows: [...this.menu.querySelectorAll('[data-conversation-id]')].map(
        (node) => node.dataset.conversationId
      ),
      chapters: [...this.menu.querySelectorAll('[data-chapter-id]')].map(
        (node) => node.dataset.chapterId
      ),
      action: active?.classList.contains('msg-menu-action-read')
        ? '.msg-menu-action-read'
        : active?.classList.contains('msg-menu-action-leave')
          ? '.msg-menu-action-leave'
          : 'a'
    };
  }

  restore(measurement) {
    if (!measurement) return;
    this.menu.scrollTop = measurement.top;
    measurement.restore(() => {
      const rows = [...this.menu.querySelectorAll('[data-conversation-id]')];
      const chapters = [...this.menu.querySelectorAll('[data-chapter-id]')];
      if (measurement.chapter) {
        const index = measurement.chapters.indexOf(measurement.chapter);
        return [
          chapters.find((node) => node.dataset.chapterId === measurement.chapter),
          chapters[index],
          chapters[index - 1],
          this.menu.querySelector('a[href], button')
        ];
      }
      const same = rows.find((node) => node.dataset.conversationId === measurement.conversation);
      const index = measurement.rows.indexOf(measurement.conversation);
      return [
        same?.querySelector(measurement.action),
        same?.querySelector('a'),
        rows[index]?.querySelector('a'),
        rows[index - 1]?.querySelector('a'),
        this.trigger
      ];
    });
  }

  toggle() {
    if (!this.connection) return;
    if (this.open) return this.close();
    this.visited = this.open = true;
    void this.source.refreshChapters();
  }

  close(restore = false) {
    this.open = false;
    if (restore) this.context.services.a11y.focusElement(this.trigger);
  }

  keydown(event) {
    if (
      event.key !== 'Escape' ||
      event.defaultPrevented ||
      event.isComposing ||
      this.context.services.a11y.hasOpenModal() ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      event.shiftKey ||
      !this.open
    )
      return;
    event.preventDefault();
    event.stopPropagation();
    this.close(true);
  }

  focusout(event) {
    if (event.relatedTarget && !this.container.contains(event.relatedTarget)) this.close();
  }

  navigate(event, id) {
    if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    this.close();
    this.context.window.navigate(`/chapters/${id}`);
  }

  all(event) {
    event.preventDefault();
    this.close();
    this.context.window.navigate('/messages');
  }

  start(group) {
    this.close();
    this.context.services.a11y.focusElement(this.trigger);
    const start = this.startChat;
    return start?.(group, this.signal);
  }

  openChat(event, conversation) {
    event.preventDefault();
    this.close();
    const target = continuumConversationHref(conversation);
    if (target) {
      this.context.window.navigate(target);
      return;
    }
    const open = this.openConversation;
    open?.(conversation.id);
  }

  async markRead(event, row) {
    event.preventDefault();
    event.stopPropagation();
    const context = this.context,
      identity = context.services.auth.getAuthContext();
    try {
      await context.services.reads.markThrough(row.conversation.id, row.latestSeq);
    } catch (error) {
      if (!context.signal.aborted && identity === context.services.auth.getAuthContext())
        return context.services.dialogs.alertDialog(
          error.message || 'Failed to mark conversation as read'
        );
    }
  }

  async leave(event, conversation) {
    event.preventDefault();
    event.stopPropagation();
    const context = this.context,
      source = this.source,
      left = this.leftConversation;
    const { auth, api, dialogs } = context.services;
    const identity = auth.getAuthContext(),
      userId = auth.getUser()?.id;
    const owns = () => !context.signal.aborted && identity === auth.getAuthContext();
    try {
      await api.del(`/api/conversations/${conversation.id}/members/${userId}`);
      if (!owns()) return;
      source.removeConversation(conversation.id);
      left?.(conversation.id);
      await source.refresh();
    } catch (error) {
      if (owns()) return dialogs.alertDialog(error.message || 'Failed to leave conversation');
    }
  }

  renderContents() {
    if (!this.visited) return null;
    return (
      <>
        <section class="msg-menu-section" aria-labelledby="msg-menu-chapters-heading">
          <h2 id="msg-menu-chapters-heading" class="msg-menu-heading">Active chapters</h2>
          <div id="msg-menu-chapters" class="chapter-activity-menu">
            <ChapterActivity state={this.chapters} onNavigate={this.navigate}/>
          </div>
        </section>
        <section class="msg-menu-section" aria-labelledby="msg-menu-chats-heading">
          <h2 id="msg-menu-chats-heading" class="msg-menu-heading">Chats</h2>
          <div class="msg-menu-chats">
            {!this.records.length && !this.invitations.length ? (
              <p class="menu-empty">No conversations yet.</p>
            ) : null}
            {this.invitations.map((invitation) => (
              <a key={invitation.key} href="/messages" class="menu-item nav-invitation-item" onClick={this.all}>
                <span class="nav-invitation-main">
                  Invitation{invitation.others ? `: ${invitation.others}` : ''}
                </span>
                {invitation.blocked ? (
                  <span class="nav-invitation-warning">You blocked {invitation.blocked}</span>
                ) : null}
                <span class="activity-dot"/>
              </a>
            ))}
            {this.records.map((row) => (
              <div key={row.conversation.id} class="msg-menu-row" data-conversation-id={row.conversation.id}>
                <a href={`/messages/${row.conversation.id}`} class="msg-menu-conversation" onClick={(event) => this.openChat(event, row.conversation)}>
                  <strong title={row.label}>{row.label}</strong>
                  <small title={row.preview}>{row.preview}</small>
                </a>
                <div class="msg-menu-meta">
                  {row.hasUnread ? (
                    <>
                      <span class="activity-dot"/>
                      <button type="button" class="msg-menu-action msg-menu-action-read" title="Mark conversation as read" aria-label="Mark conversation as read" command={(event) => this.markRead(event, row)}>
                        <Icon name="check" classes="icon-xs"/>
                      </button>
                    </>
                  ) : null}
                  {row.canLeave ? (
                    <button type="button" class="msg-menu-action msg-menu-action-leave" title="Leave conversation" aria-label="Leave conversation" command={(event) => this.leave(event, row.conversation)}>
                      <Icon name="close" classes="icon-xs"/>
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
            <div class="menu-divider-top">
              <button type="button" class="nav-menu-button" onClick={() => this.start(false)}>
                Start Chat...
              </button>
              <button type="button" class="nav-menu-button" onClick={() => this.start(true)}>
                Start Group Chat...
              </button>
              <a href="/messages" class="nav-menu-item" onClick={this.all}>View all messages →</a>
            </div>
          </div>
        </section>
      </>
    );
  }

  render() {
    const snapshot = this.snapshot;
    if (!snapshot) return null;
    return (
      <div ref={this.captureContainer} class="messages-menu-container" id="msg-menu-container" onKeyDown={this.keydown} onFocusout={this.focusout}>
        <button ref={this.captureTrigger} id="msg-menu-button" class="nav-icon-button messages-nav-button" title={snapshot.label} aria-label={snapshot.label} aria-controls="msg-menu" aria-expanded={String(this.open)} onClick={this.toggle}>
          <Icon name="message-dots" classes="icon-sm"/>
          <span id="msg-unread" class="messages-nav-badge">
            {snapshot.dot ? (
              <span
                class={`activity-dot${snapshot.pulse ? ' chapter-activity-pulse' : ''}`}
                aria-hidden="true"/>
            ) : null}
          </span>
        </button>
        <div ref={this.captureMenu} id="msg-menu" class="msg-menu-dropdown" hidden={!this.open}>
          {this.renderContents()}
        </div>
      </div>
    );
  }
}

export function registerInboxMenu(registry) {
  defineUIElements(registry, { 'tbc-inbox-menu': InboxMenu });
}
